import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { currentBusinessDate } from '../utils/casinoDay';

// ----------------------------------------------------------------
// CageDateContext
// ----------------------------------------------------------------
// One working day shared by every Cage Balancing tab - but the tabs do not all
// show the SAME business date, because they are not all entered for the same
// gaming day.
//
// On a given morning the cage enters:
//    Balance Sheet   for that morning's gaming day        (offset  0)
//    Drop Count      for that morning's gaming day        (offset  0)
//    Free Play       for the gaming day that just closed  (offset -1)
//    Chip Liability  for the gaming day that just closed  (offset -1)
//
// So the context stores the ANCHOR - the offset-0 day, i.e. the one Balance
// Sheet and Drop Count are on - and each tab derives its own business date from
// it via useCageDate(offsetDays). Moving from Balance Sheet to Chip Liability
// therefore lands on the right sheet with no date shuffling, and stepping the
// date on either tab keeps the pair in step.
//
// Two other behaviours worth knowing:
//  * The anchor defaults to the CASINO business date (casinoDay.currentBusinessDate),
//    which rolls over at 06:00 - not the calendar date. A plain calendar date
//    would open the wrong gaming day before 6 AM.
//  * It is kept in sessionStorage, so it survives tab navigation and a reload
//    but a brand-new session still starts on the current gaming day.

const STORAGE_KEY = 'cage.anchorDate';
const CageDateContext = createContext(null);

const isYmd = (v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);

/** Shift a YYYY-MM-DD by whole days without ever touching local timezone. */
export function shiftDate(ymd, days) {
  if (!days) return ymd;
  const [y, m, d] = ymd.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d));
  t.setUTCDate(t.getUTCDate() + days);
  return t.toISOString().slice(0, 10);
}

/**
 * How far each tab sits from the anchor day. Exported so a page and its date
 * picker can name the same constant instead of repeating a magic -1.
 */
export const CAGE_OFFSET = {
  /** Entered for the gaming day in progress. */
  SAME_DAY: 0,
  /** Entered the morning after, for the gaming day that just closed. */
  CLOSED_DAY: -1,
};

export function CageDateProvider({ children }) {
  const [anchorDate, setAnchor] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (isYmd(saved)) return saved;
    } catch {
      /* storage blocked (private mode) - fall through to today */
    }
    return currentBusinessDate();
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, anchorDate);
    } catch {
      /* non-fatal: the date just won't survive a reload */
    }
  }, [anchorDate]);

  const setAnchorDate = useCallback((v) => {
    if (isYmd(v)) setAnchor(v);
  }, []);
  const stepDays = useCallback((n) => setAnchor((d) => shiftDate(d, n)), []);
  const goToday = useCallback(() => setAnchor(currentBusinessDate()), []);

  const value = useMemo(
    () => ({ anchorDate, setAnchorDate, stepDays, goToday, todayAnchor: currentBusinessDate() }),
    [anchorDate, setAnchorDate, stepDays, goToday]
  );

  return <CageDateContext.Provider value={value}>{children}</CageDateContext.Provider>;
}

/**
 * The business date for THIS tab.
 *
 * @param {number} offsetDays days from the anchor - use CAGE_OFFSET.
 * @returns businessDate for the tab, plus setters that translate back to the
 *          anchor so every other tab moves with it.
 *
 * `today` is the date this tab would normally be showing right now (the anchor's
 * current gaming day, offset applied), so the picker's "Today" button and its
 * relative badge read correctly on a tab that legitimately trails by a day.
 */
export function useCageDate(offsetDays = 0) {
  const ctx = useContext(CageDateContext);
  if (!ctx) throw new Error('useCageDate must be used inside a CageDateProvider');
  const { anchorDate, setAnchorDate, stepDays, goToday, todayAnchor } = ctx;

  return useMemo(
    () => ({
      businessDate: shiftDate(anchorDate, offsetDays),
      setBusinessDate: (v) => {
        if (isYmd(v)) setAnchorDate(shiftDate(v, -offsetDays));
      },
      stepDays,
      goToday,
      today: shiftDate(todayAnchor, offsetDays),
      anchorDate,
      offsetDays,
    }),
    [anchorDate, setAnchorDate, stepDays, goToday, todayAnchor, offsetDays]
  );
}
